// accessControl.ts — منطق الصلاحيّات (منقول بلا تغيير سلوكيّ من frontend/src/utils/accessControl.js).
import { readJsonStorage, readStorage } from './storage';

export interface Permission {
	page_name: string;
	permission_key?: string;
	can_access: 0 | 1;
}

export interface MenuEntry {
	path?: string;
	route?: string;
	is_active?: number;
}

export function compactKey(value: unknown): string {
	return String(value ?? '')
		.toLowerCase()
		.split('?')[0]
		.split('#')[0]
		.replace(/[^a-z0-9]/g, '');
}

/** true للجلسة «مدير النظام» (role_id=1) — يتجاوز كل فحوص الصلاحيّات. */
export function isSystemAdminSession(): boolean {
	const roleId = Number(readStorage('role_id') || 0);
	return roleId === 1;
}

export function getPathCandidates(pathname = '/'): string[] {
	const segments = String(pathname || '')
		.split('?')[0]
		.split('#')[0]
		.replace(/^\/+/, '')
		.split('/')
		.filter(Boolean);

	const candidates: string[] = [];
	if (segments[0]) candidates.push(segments[0]);
	if (segments[0] && segments[1]) candidates.push(`${segments[0]}/${segments[1]}`);
	if (segments[0] && segments[1]) candidates.push(segments[1]);
	if (segments.length > 2) candidates.push(segments.join('/'));

	const routeAliases: Record<string, string[]> = { horse: ['horse-nfc'] };
	(routeAliases[segments[0]] || []).forEach((alias) => candidates.push(alias));

	return [...new Set(candidates.map(compactKey).filter(Boolean))];
}

export function hasPagePermission(
	permissions: Permission[] = [],
	requiredKeys: string[] = [],
	pathname = '/',
): boolean {
	const have = new Set(
		(permissions || [])
			.filter((permission) => Number(permission?.can_access) === 1)
			.flatMap((permission) => [permission.page_name, permission.permission_key])
			.filter(Boolean)
			.map(compactKey),
	);

	const required = (requiredKeys || []).filter(Boolean).map(compactKey);
	const candidates = required.length ? required : getPathCandidates(pathname);

	return candidates.some((candidate) => have.has(candidate));
}

/** فحص صلاحيّة على مستوى الفعل (زرّ/عنصر داخل الصفحة) لا التوجيه. */
export function hasActionPermission(key?: string): boolean {
	if (!key) return false;
	if (isSystemAdminSession()) return true;
	const perms = readJsonStorage<Permission[]>('permissions', []);
	return hasPagePermission(perms, [key]);
}

export function isAllowedByDesignedMenu(pathname = '/'): boolean {
	const allMenu = readJsonStorage<MenuEntry[]>('app_menu_all', []);
	const allowedMenu = readJsonStorage<MenuEntry[]>('me_menu', []);

	if (!Array.isArray(allMenu) || allMenu.length === 0) return true;
	if (!Array.isArray(allowedMenu)) return true;

	const candidates = new Set(getPathCandidates(pathname));
	const managed = new Set(
		allMenu
			.filter((item) => Number(item?.is_active) === 1)
			.map((item) => compactKey(item.path || item.route))
			.filter(Boolean),
	);

	const isManagedRoute = [...candidates].some((candidate) => managed.has(candidate));
	if (!isManagedRoute) return true;
	if (allowedMenu.length === 0) return true;

	const allowed = new Set(allowedMenu.map((item) => compactKey(item.path || item.route)).filter(Boolean));
	return [...candidates].some((candidate) => allowed.has(candidate));
}

export function isOwnerRouteAllowed(pathname = '/'): boolean {
	const ownerId = readStorage('owner_id');
	const hasOwnerId = Boolean(ownerId && ownerId !== 'null' && ownerId !== 'undefined');
	const roleId = Number(readStorage('role_id') || 0);
	const isSystemAdmin = Number(readStorage('is_system_admin') || 0) === 1;
	const isOwnerAccount = hasOwnerId && (roleId === 3 || isSystemAdmin);

	if (!isOwnerAccount) return false;
	const ownerAllowed = new Set(['myhorses', 'myhorsestats']);
	const isOwnerSelfRoute = getPathCandidates(pathname).some((candidate) => ownerAllowed.has(candidate));
	if (!isOwnerSelfRoute) return false;

	const permissions = readJsonStorage<Permission[]>('permissions', []);
	return hasPagePermission(permissions, [], pathname);
}
